import { fromApiBrDate } from '@/features/campaigns/model/campaign-dates';
import { fromServerDate } from '@/shared/lib/server-date';

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Dom: 0,
  Seg: 1,
  Ter: 2,
  Qua: 3,
  Que: 4,
  Sex: 5,
  Sab: 6,
};

export interface NextRunInput {
  frequencia?: string;
  frequencia_horas?: string;
  frequencia_dia?: string | number;
  frequencia_dia_semana?: string;
  frequencia_unico?: string;
  expirar?: boolean;
  dt_fim?: string | Date | null;
  ativo?: boolean;
}

function parseHours(horas?: string): { hh: number; mm: number } {
  const [hh = 0, mm = 0] = (horas ?? '00:00')
    .trim()
    .slice(0, 5)
    .split(':')
    .map((x) => Number(x) || 0);
  return { hh, mm };
}

function atLocal(y: number, m: number, d: number, hh: number, mm: number): Date {
  return new Date(y, m, d, hh, mm, 0, 0);
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/** Campanha com expirar=true e dt_fim no passado não entra na agenda. */
export function isCampaignExpired(row: NextRunInput, now = new Date()): boolean {
  if (row.expirar !== true || !row.dt_fim) return false;
  const raw = typeof row.dt_fim === 'string' ? row.dt_fim : fromServerDate(row.dt_fim);
  const ymd = fromApiBrDate(raw);
  if (ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    // Fim do dia em UTC — alinhado ao setUTCHours da API.
    const end = new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999));
    return end.getTime() < now.getTime();
  }
  const end = new Date(row.dt_fim);
  if (Number.isNaN(end.getTime())) return false;
  return end.getTime() < now.getTime();
}

/**
 * Próximo disparo absoluto a partir dos campos de frequência do legado.
 * Retorna null se não houver próximo disparo (única no passado, dados inválidos, expirada).
 */
export function computeNextRunAt(row: NextRunInput, now = new Date()): Date | null {
  if (isCampaignExpired(row, now)) return null;

  const { hh, mm } = parseHours(row.frequencia_horas);
  const freq = String(row.frequencia ?? '');

  if (freq === '31') {
    const ymd = fromApiBrDate(row.frequencia_unico);
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    const when = atLocal(y, m - 1, d, hh, mm);
    return when.getTime() > now.getTime() ? when : null;
  }

  if (freq === '1') {
    const today = atLocal(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
    if (today.getTime() > now.getTime()) return today;
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  if (freq === '7') {
    const target = WEEKDAY_TO_INDEX[String(row.frequencia_dia_semana ?? '').trim()];
    if (target == null) return null;
    const candidate = atLocal(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
    let delta = (target - now.getDay() + 7) % 7;
    if (delta === 0 && candidate.getTime() <= now.getTime()) delta = 7;
    candidate.setDate(candidate.getDate() + delta);
    return candidate;
  }

  if (freq === '30') {
    const dayNum = Number(row.frequencia_dia);
    if (!Number.isFinite(dayNum) || dayNum < 1 || dayNum > 31) return null;

    const tryMonth = (year: number, month: number): Date | null => {
      const lastDay = new Date(year, month + 1, 0).getDate();
      const day = Math.min(dayNum, lastDay);
      return atLocal(year, month, day, hh, mm);
    };

    const thisMonth = tryMonth(now.getFullYear(), now.getMonth());
    if (thisMonth && thisMonth.getTime() > now.getTime()) return thisMonth;

    const nextMonthIndex = now.getMonth() + 1;
    const nextYear = now.getFullYear() + Math.floor(nextMonthIndex / 12);
    const nextMonth = nextMonthIndex % 12;
    return tryMonth(nextYear, nextMonth);
  }

  return null;
}

export type AgendaWindow = 'today' | '7d' | 'all';

export function isNextRunInWindow(
  nextRunAt: Date | null,
  window: AgendaWindow,
  now = new Date(),
): boolean {
  if (window === 'all') return true;
  if (!nextRunAt) return false;
  const t = nextRunAt.getTime();
  if (window === 'today') {
    return t >= startOfLocalDay(now).getTime() && t <= endOfLocalDay(now).getTime();
  }
  // próximos 7 dias (a partir de agora até fim do 7º dia)
  const end = endOfLocalDay(now);
  end.setDate(end.getDate() + 6);
  return t >= now.getTime() && t <= end.getTime();
}

export function formatNextRunLabel(nextRunAt: Date | null): string {
  if (!nextRunAt) return 'Sem próximo disparo';
  const date = nextRunAt.toLocaleDateString('pt-BR');
  const time = nextRunAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}
